import { CompsCellRenderer } from "./CompsCellRenderer";
import { DISABLED_COMMISSION_TYPE_VALUES } from "./compsConstants";
import { formatCommissionDisplay } from "./formatting/formatCommissionDisplay";
import { INCUMBENT_COL_KEY } from "./types";

import type { CompsTableCarrierRef } from "@platform/api";
import type {
  ColDef,
  EditableCallbackParams,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from "ag-grid-community";
import type {
  CarrierCommissionCell,
  CompsGridContext,
  CompsGridRow,
} from "./types";

// ---------------------------------------------------------------------------
// Column definition builder helpers
// ---------------------------------------------------------------------------

const isCellEditableBasedOnStatus = (cell: CarrierCommissionCell): boolean => {
  if (cell.proposalStatus === "pending") return false;
  if (cell.coverageStatus === "declinedToQuote") return false;
  if (
    cell.commissionType &&
    DISABLED_COMMISSION_TYPE_VALUES.includes(cell.commissionType)
  ) {
    return false;
  }
  return true;
};

const hasEditAccessBasedOnProposalOrTarget = (
  carrier: CompsTableCarrierRef,
  row: CompsGridRow,
  context?: CompsGridContext,
): boolean => {
  const hasProposalPlan = !!context?.proposalPlanIdByCellKey?.get(
    `${carrier.id}:${row.rowGroupId}`,
  );
  const hasMaterializeTarget = !!context?.materializeTargetByRowGroupId?.get(
    row.rowGroupId,
  );
  return hasProposalPlan || hasMaterializeTarget;
};

// ---------------------------------------------------------------------------
// Column definition builder
// ---------------------------------------------------------------------------

export const buildCarrierColDef = (
  carrier: CompsTableCarrierRef,
): ColDef<CompsGridRow> => ({
  field: carrier.id,
  headerName: carrier.name,
  minWidth: 140,
  flex: 1,
  cellRenderer: CompsCellRenderer,

  valueGetter: (params: ValueGetterParams<CompsGridRow>) => {
    const row = params.data;
    if (!row) return undefined;
    return row[carrier.id];
  },

  valueFormatter: (params: ValueFormatterParams<CompsGridRow>) => {
    const row = params.data;
    if (!row) return "";
    const cell = row[carrier.id];
    if (!cell) return "";
    if (typeof cell === "string") return cell;
    if (row.rowType !== "commission") return "";
    return formatCommissionDisplay(
      cell,
      cell.proposalStatus,
      cell.coverageStatus,
    );
  },

  editable: (params: EditableCallbackParams<CompsGridRow>) => {
    const row = params.data;
    if (!row) return false;
    if (row.rowType === "notes" || row.rowType === "subsidy") return true;
    if (row.rowType !== "commission") return false;
    if (carrier.id === INCUMBENT_COL_KEY) return false;

    const cell = row[carrier.id];
    if (!cell || typeof cell === "string") return false;
    if (!isCellEditableBasedOnStatus(cell)) return false;

    const context = params.context as CompsGridContext;
    // Editable when the cell already has a proposal plan (carrier/analyst quote)
    // OR when the coverage row carries a materialize target — i.e. an unquoted
    // cell the analyst can populate by authoring an analyst proposal plan (ADR-024).
    return hasEditAccessBasedOnProposalOrTarget(carrier, row, context);
  },

  cellEditorSelector: (params) => {
    const row = params.data;
    if (!row) return undefined;

    if (row.rowType === "notes" || row.rowType === "subsidy") {
      return { component: "agTextCellEditor" };
    }

    const cell = row[carrier.id];
    if (!cell || typeof cell === "string") return undefined;

    return { component: "EditCommissionEditor" };
  },

  // Popup editor opened by double-click (the AG Grid default) — no
  // `singleClickEdit`, matching the Edit Benefit popup convention in Quotes.

  valueSetter: (params: ValueSetterParams<CompsGridRow>) => {
    const row = params.data;
    if (!row) return false;

    if (row.rowType === "notes" || row.rowType === "subsidy") {
      row[carrier.id] = params.newValue as string;
      return true;
    }

    const cell = row[carrier.id];
    if (!cell || typeof cell === "string") return false;

    // Delete-key clear (see CompsGrid's onCellKeyDown) sends `null`; Save from
    // EditCommissionEditor sends the full updated cell.
    const newValue = params.newValue as CarrierCommissionCell | null;
    if (newValue === null) {
      cell.commissionType = null;
      cell.amount = null;
    } else {
      cell.commissionType = newValue.commissionType;
      cell.amount = newValue.amount;
    }

    return true;
  },
});
